// ENG-5357: agt-manager's Claude Code upgrade is gated by a 24h disk
// marker so a manager restart can't fire `brew upgrade --cask claude-code`
// every tick (a restart storm that once pegged alyve-host-1 until reboot).
//
// These three functions are the load-bearing safety property. They are
// split out of manager-worker.ts (ENG-6418) into this dependency-free
// module so the unit test can import them without evaluating the entire
// 13k-line manager-worker graph (+ all of @augmented/core) — that one-time
// module evaluation was landing inside the first test's 5s budget and
// flaking under parallel suite load. manager-worker.ts re-exports them, so
// the public surface is unchanged.

import { mkdirSync, readFileSync, writeFileSync } from 'node:fs';
import { dirname, join } from 'node:path';
import { homedir } from 'node:os';

export const CLAUDE_CODE_UPGRADE_CHECK_INTERVAL_MS = 24 * 60 * 60 * 1000; // 24h

export function claudeCodeUpgradeMarkerPath(): string {
  return join(homedir(), '.augmented', '.last-claude-code-upgrade-check');
}

export function stampClaudeCodeUpgradeMarker(): void {
  try {
    // Ensure $HOME/.augmented exists before writing. The marker is the
    // load-bearing throttle: if the write silently fails (ENOENT) the
    // throttle reads false forever and the brew-upgrade restart storm
    // (ENG-5357) returns. Self-contained, so we don't rely on a caller
    // having pre-created the directory.
    const markerPath = claudeCodeUpgradeMarkerPath();
    mkdirSync(dirname(markerPath), { recursive: true });
    writeFileSync(markerPath, String(Date.now()));
  } catch { /* non-fatal — next poll will try again */ }
}

export function claudeCodeUpgradeThrottled(): boolean {
  try {
    const lastCheck = parseInt(readFileSync(claudeCodeUpgradeMarkerPath(), 'utf-8').trim(), 10);
    if (!Number.isFinite(lastCheck)) return false;
    return Date.now() - lastCheck < CLAUDE_CODE_UPGRADE_CHECK_INTERVAL_MS;
  } catch {
    // No marker yet → not throttled → first call writes it.
    return false;
  }
}
